import React, { useEffect, useState } from "react";
import commonStyles from "@styles/CommonPageLayout.module.css";
import { useSearchParams } from "react-router-dom";
import MemorialPDFDownloadButton from "@components/misc/MemorialPDF/MemorialPDFDownloadButton";
import MemorialPDFViewer from "@components/misc/MemorialPDF/MemorialPDFViewer";
import LoadSaveErrorDisplay from "@components/display/LoadSaveErrorDisplay/LoadSaveErrorDisplay";
import { Memorial } from "@models/Memorial";
import { Media } from "@models/Media";
import { fetchMemorial } from "@services/memorialService";
import { fetchBusiness } from "@services/businessService";
import { captureVideoPosters } from "@util/video-poster";

const collectVideoSources = (memorial: Memorial): string[] => {
    const media: (Media | undefined)[] = [
        ...memorial.tributes.filter((t) => !t.forReview).map((t) => t.media),
        ...memorial.timeline.map((t) => t.media),
        ...memorial.gallery.flatMap((section) => section.media),
    ];
    return media.filter((m): m is Media => m?.type === "video").map((m) => m.src);
};

const MemorialExportPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const id = searchParams.get("id");

    const [memorial, setMemorial] = useState<Memorial>();
    const [businessLogo, setBusinessLogo] = useState("");
    const [videoPosters, setVideoPosters] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                if (!id) throw new Error("No ID.");
                const data = await fetchMemorial(id);
                if (data.businessId) {
                    const business = await fetchBusiness(data.businessId);
                    setBusinessLogo(business.logo || "");
                }
                // Videos can't be embedded in a PDF, so capture a frame from each to show instead
                setVideoPosters(await captureVideoPosters(collectVideoSources(data)));
                setMemorial(data);
            } catch {
                setError("Failed to fetch memorial.");
            } finally {
                setLoading(false);
            }
        };
        void fetchData();
    }, [id]);

    const renderContent = () => {
        if (loading || error) {
            return <LoadSaveErrorDisplay isLoading={loading} error={error} />;
        }

        if (!memorial) {
            return <div className={`${commonStyles.noContent} ${commonStyles.note}`}>No memorial</div>;
        }

        const logo = businessLogo || "/assets/images/fioh-text-logo.png";

        return (
            <>
                <div className={commonStyles.headerContainer}>
                    <h1>{`${memorial.firstName} ${memorial.lastName} - Export PDF`}</h1>
                    <div className={commonStyles.buttonGroup}>
                        <MemorialPDFDownloadButton
                            memorial={memorial}
                            businessLogo={logo}
                            videoPosters={videoPosters}
                        />
                    </div>
                </div>
                <MemorialPDFViewer
                    memorial={memorial}
                    businessLogo={logo}
                    videoPosters={videoPosters}
                />
            </>
        );
    };

    return <div className={commonStyles.container}>{renderContent()}</div>;
};

export default MemorialExportPage;
